import classNames from 'classnames'
import type { ChangeEvent, FocusEvent, RefObject } from 'react'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { CrossIcon } from '../../icon'
import styles from './BaseInput.module.scss'
import type { BaseInputProps } from './types'
import { InputSize, InputType, InputVariant } from './types'

const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>((props, ref) => {
  const {
    'aria-activedescendant': ariaActivedescendant,
    'aria-controls': ariaControls,
    'aria-describedby': ariaDescribedby,
    'aria-errormessage': ariaErrormessage,
    'aria-expanded': ariaExpanded,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    disabled = false,
    leadingAccessory,
    leadingIcon: LeadingIcon,
    onBlur,
    onClick,
    onChange,
    onFocus,
    onKeyDown,
    placeholder,
    prefix,
    readonly = false,
    role,
    rtl = false,
    size = InputSize.medium,
    suffix,
    trailingAccessory,
    trailingIcon: TrailingIcon,
    type = InputType.text,
    value,
    variant = InputVariant.neutral,
    clearable = true,
    autoComplete,
  } = props
  const _ref = useRef(null)
  const inputRef = ref ?? _ref
  const [isFocused, setFocused] = useState(false)
  const [showClearIcon, setShowClearIcon] = useState(false)
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(e)
    },
    [onChange]
  )
  const handleFocus = (e: FocusEvent<HTMLInputElement, Element>) => {
    setFocused(true)
    onFocus && onFocus(e)
  }
  const handleBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    setFocused(false)
    onBlur && onBlur(e)
  }

  const handleClear = useCallback(() => {
    const currentInput = (inputRef as RefObject<HTMLInputElement>)?.current
    if (!currentInput) return
    currentInput.value = ''
    handleChange({
      target: { value: '' },
      currentTarget: { value: '' },
    } as ChangeEvent<HTMLInputElement>)
  }, [inputRef, handleChange])

  useEffect(() => {
    let blurTimeout: NodeJS.Timeout
    if (readonly) {
      setShowClearIcon(false)
    } else if (isFocused) {
      setShowClearIcon(!!value?.length)
    } else {
      blurTimeout = setTimeout(() => {
        setShowClearIcon(false)
      }, 200)
    }
    return () => {
      blurTimeout && clearTimeout(blurTimeout)
    }
  }, [isFocused, value, readonly])

  const leaadingAccessoryComponent =
    leadingAccessory || (LeadingIcon && <LeadingIcon rtl={rtl} outline key="leadingIcon" />)
  const trailingAccessoryComponent =
    trailingAccessory || (TrailingIcon && <TrailingIcon rtl={rtl} outline key="trailingIcon" />)
  return (
    <div
      className={classNames(styles['pintxo-base-input'], props.className)}
      data-size={size}
      data-variant={variant}
      data-disabled={disabled}
      data-focused={isFocused}
      data-rtl={rtl}
    >
      {leaadingAccessoryComponent}
      {prefix && (
        <span
          className={classNames(styles['pintxo-base-input__prefix'], 'pintxo-typography-body1')}
          key="prefix"
        >
          {prefix}
        </span>
      )}
      <input
        ref={inputRef}
        className={classNames(styles['pintxo-base-input__input'], 'pintxo-typography-body1')}
        readOnly={readonly}
        disabled={disabled}
        data-rtl={rtl}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        key="input"
        type={type}
        autoComplete={autoComplete}
        onBlur={handleBlur}
        onClick={onClick}
        onInput={handleChange}
        onFocus={handleFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        value={value}
        {...(role ? { role } : {})}
        {...(ariaControls ? { 'aria-controls': ariaControls } : {})}
        {...(ariaExpanded ? { 'aria-expanded': !!ariaExpanded } : {})}
        {...(ariaActivedescendant ? { 'aria-activedescendant': ariaActivedescendant } : {})}
        {...(ariaErrormessage
          ? { 'aria-errormessage': ariaErrormessage, 'aria-invalid': true }
          : {})}
        {...(ariaDescribedby ? { 'aria-describedby': ariaDescribedby } : {})}
      />
      {clearable && (
        <CrossIcon
          key="deleteIcon"
          onClick={handleClear}
          className={classNames(styles['clearIcon'], showClearIcon ? styles['visible'] : '')}
        />
      )}

      {suffix && (
        <span
          className={classNames(styles['pintxo-base-input__suffix'], 'pintxo-typography-body1')}
          key="suffix"
        >
          {suffix}
        </span>
      )}
      {trailingAccessoryComponent}
    </div>
  )
})
BaseInput.displayName = 'BaseInput'
export default BaseInput
